import { ReactNode, useEffect, useMemo, useRef } from "react";
import { useAuth } from "react-oidc-context";

import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client";
import { ServerError } from "@apollo/client/errors";
import { setContext } from "@apollo/client/link/context";
import { RetryLink } from "@apollo/client/link/retry";
import { ApolloProvider } from "@apollo/client/react";

export function ApolloClientProvider({ children }: { children: ReactNode }) {
	const auth = useAuth();
	const authRef = useRef({
		getToken: () => auth.user?.access_token,
		signinSilent: () => auth.signinSilent(),
	});

	useEffect(() => {
		authRef.current = {
			getToken: () => auth.user?.access_token,
			signinSilent: () => auth.signinSilent(),
		};
	}, [auth]);

	const client = useMemo(() => {
		const httpLink = new HttpLink({
			uri: import.meta.env.VITE_GRAPHQL_ENDPOINT || "/graphql",
		});

		const authLink = setContext(async (_, { headers }) => {
			const token = authRef.current.getToken();
			let Authorization = "";

			if (token) {
				Authorization = "Bearer " + token;
				window.token = Authorization;
			}

			return {
				headers: {
					...headers,
					Authorization,
				},
			};
		});

		const retryLink = new RetryLink({
			delay: {
				initial: 300,
				max: Infinity,
				jitter: true,
			},
			attempts: {
				max: 5,
				retryIf: async (error, _operation) => {
					if (ServerError.is(error) && error.statusCode === 401) {
						await authRef.current.signinSilent();
						return true;
					}
					return false;
				},
			},
		});

		return new ApolloClient({
			link: ApolloLink.from([retryLink, authLink, httpLink]),
			cache: new InMemoryCache({
				typePolicies: {
					User: {
						keyFields: ["userId"],
					},
				},
			}),
			defaultOptions: {
				watchQuery: {
					fetchPolicy: "cache-and-network",
				},
			},
		});
	}, []);

	return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
